import type { FC, PropsWithChildren, ReactNode } from 'react'
import { Button } from 'react-aria-components'
import type { VariantProps } from 'tailwind-variants'
import { tv } from 'tailwind-variants'
import IcOutlineHelpOutline from '~icons/ic/outline-help-outline'
import { UIDialog } from 'src/components/ui/Modal'
import { cx } from 'src/utils/cx'
import { useHoverMediaQuery } from 'src/utils/hooks/useHoverMediaQuery'
import { HelpPopover } from './HelpPopover'
import type { UITooltipTriggerWrapperProps } from './ui/Tooltip'
import { UITooltipTriggerWrapper } from './ui/Tooltip'

export type HelpPopoverProps = PropsWithChildren<{
  scene?: string
  className?: string
  trigger?: ReactNode
}>

/**
 * @deprecated use UITooltipHelpIcon instead
 * @param param0
 * @returns
 */
const HelpIcon: FC<
  {
    iconClassname?: string
    iconComp?: typeof IcOutlineHelpOutline
  } & HelpPopoverProps
> = ({
  iconClassname,
  iconComp: IconComp = IcOutlineHelpOutline,
  ...props
}) => {
  return (
    <HelpPopover {...props} trigger={<IconComp className={iconClassname} />} />
  )
}

export default HelpIcon

const helpTooltipStyles = tv({
  slots: {
    tooltip: 'overflow-auto',
    modal: 'overflow-auto',
    dialog: 'flex-col flex outline-none',
  },
  variants: {
    variant: { normal: '' },
    showClose: { false: '' },
    showConfirm: { false: '' },
  },
  compoundVariants: [
    {
      variant: 'normal',
      class: {
        tooltip: 'p-4',
        dialog: '',
        modal: 'p-4',
      },
    },
    {
      variant: 'normal',
      showClose: true,
      class: {
        modal: 'pt-0',
      },
    },
    {
      variant: 'normal',
      showConfirm: true,
      class: {
        modal: 'pb-0',
      },
    },
  ],
})

export type UITooltipResponsiveProps = PropsWithChildren<{
  wrapperClassName?: string
  variant?: VariantProps<typeof helpTooltipStyles>['variant']
  TooltipProps?: UITooltipTriggerWrapperProps['TooltipProps'] & {
    placement?: UITooltipTriggerWrapperProps['placement']
    delay?: UITooltipTriggerWrapperProps['delay']
  }
  /** Trigger has to be an RAC Button. Otherwise tooltip won't display at all. */
  trigger: (open?: () => void) => JSX.Element
}>
/**
 * Uses Popover if user's device can't hover
 * Uses Tooltip otherwise
 * @param props
 * @returns
 */
export function UITooltipResponsive(props: UITooltipResponsiveProps) {
  const canHover = useHoverMediaQuery()

  const { variant, wrapperClassName, TooltipProps, trigger } = props

  return canHover ? (
    <UITooltipTriggerWrapper
      delay={TooltipProps?.delay ?? 250}
      TooltipProps={TooltipProps}
      trigger={trigger()}
      /**
       * NOTE:
       * Have to make the wrapper a flex container with a scrollable child
       * Otherwise, `OverlayArrow` occupies extra size in one of four sides
       * Since it doesn't like overflowable container
       */
      className="flex"
    >
      <div
        className={helpTooltipStyles().tooltip({
          variant,
          className: wrapperClassName,
        })}
      >
        {props.children}
      </div>
    </UITooltipTriggerWrapper>
  ) : (
    trigger(() =>
      UIDialog.alert({ content: <>{props.children}</>, type: 'dialog' }),
    )
  )
}

export type UITooltipHelpIconProps = Omit<
  UITooltipResponsiveProps,
  'trigger'
> & {
  iconClassName?: string
  iconComp?: typeof IcOutlineHelpOutline
}
/**
 * Convenient wrapper for UITooltipResponsive
 * Default trigger is a HelpIcon
 *
 * @param props
 * @returns
 */
export function UITooltipHelpIcon(props: UITooltipHelpIconProps) {
  const {
    iconComp: IconComp = IcOutlineHelpOutline,
    iconClassName,
    ...rest
  } = props

  const trigger = (open?: () => void) => (
    <Button onPress={open}>
      <IconComp className={cx('size-4', iconClassName)} />
    </Button>
  )

  return <UITooltipResponsive {...rest} trigger={trigger} />
}
